import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import { formatDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, UserPlus, Link2, Power } from "lucide-react";
import { toast } from "sonner";

export default function UsersPage() {
  const [users, setUsers] = useState(null);
  const [firms, setFirms] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "advisor", firm_id: "" });

  const load = () => api.get("/users").then(({ data }) => setUsers(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => {
    load();
    api.get("/firms").then(({ data }) => setFirms(data));
  }, []); // eslint-disable-line

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: form.name, email: form.email, role: form.role };
      if (form.role === "customer") payload.firm_id = form.firm_id;
      const { data } = await api.post("/users", payload);
      toast.success("Hesap oluşturuldu. Parola belirleme bağlantısı 'Giden E-postalar' ekranında.");
      setOpen(false);
      setForm({ name: "", email: "", role: "advisor", firm_id: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const resend = async (id) => {
    try { await api.post(`/users/${id}/resend-invite`); toast.success("Bağlantı yeniden oluşturuldu (Giden E-postalar)."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = async (u) => {
    try { await api.post(`/users/${u.id}/${u.is_active ? "deactivate" : "activate"}`); toast.success("Durum güncellendi."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold text-slate-900">Kullanıcılar</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-sky-600 hover:bg-sky-700" data-testid="add-user-button"><UserPlus className="h-4 w-4 mr-2" /> Hesap Aç</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display">Yeni Hesap</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div className="space-y-1.5"><Label>Ad Soyad</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="user-name-input" /></div>
              <div className="space-y-1.5"><Label>E-posta</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="user-email-input" /></div>
              <div className="space-y-1.5">
                <Label>Rol</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="org_manager">Kuruluş Yöneticisi</SelectItem>
                    <SelectItem value="advisor">TMGD Danışmanı</SelectItem>
                    <SelectItem value="customer">Müşteri Yetkilisi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.role === "customer" && (
                <div className="space-y-1.5">
                  <Label>Firma</Label>
                  <Select value={form.firm_id} onValueChange={(v) => setForm({ ...form, firm_id: v })}>
                    <SelectTrigger data-testid="user-firm-select"><SelectValue placeholder="Firma seçin" /></SelectTrigger>
                    <SelectContent>{firms.map((f) => <SelectItem key={f.id} value={f.id}>{f.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <p className="text-xs text-slate-400">Parola, kullanıcıya gönderilen bağlantı ile belirlenir. Test modunda bağlantı 'Giden E-postalar' ekranında listelenir.</p>
              <DialogFooter><Button type="submit" className="bg-sky-600 hover:bg-sky-700" disabled={saving} data-testid="user-save-button">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Oluştur</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {users === null ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Ad Soyad</TableHead><TableHead>E-posta</TableHead><TableHead>Rol</TableHead>
                <TableHead>Parola</TableHead><TableHead>Durum</TableHead><TableHead className="text-right">İşlem</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name} {u.is_protected && <StatusBadge tone="info">Korumalı</StatusBadge>}</TableCell>
                    <TableCell className="text-slate-600">{u.email}</TableCell>
                    <TableCell>{ROLE_LABELS[u.role]}</TableCell>
                    <TableCell>{u.has_password ? <StatusBadge tone="active">Belirlendi</StatusBadge> : <StatusBadge tone="pending">Belirlenmedi</StatusBadge>}</TableCell>
                    <TableCell><StatusBadge tone={u.is_active ? "active" : "passive"}>{u.is_active ? "Aktif" : "Pasif"}</StatusBadge></TableCell>
                    <TableCell className="text-right space-x-1">
                      {!u.is_protected && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => resend(u.id)} data-testid={`user-resend-${u.id}`}><Link2 className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => toggle(u)} data-testid={`user-toggle-${u.id}`} className={u.is_active ? "text-rose-600" : "text-emerald-600"}><Power className="h-4 w-4" /></Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
